<template>
  <div class="w-full">
    <!-- Label -->
    <label
      v-if="label"
      :for="inputId"
      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
    >
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <!-- Standard Select (non-searchable) -->
    <select
      v-if="!searchable && !multiple"
      :id="inputId"
      :value="modelValue"
      @change="handleChange"
      :disabled="disabled"
      :required="required"
      :class="[
        'block w-full rounded-md shadow-sm transition-colors',
        'border-gray-300 dark:border-gray-600',
        'bg-white dark:bg-gray-800',
        'text-gray-900 dark:text-gray-100',
        'focus:border-blue-500 focus:ring-blue-500',
        disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900' : '',
        error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '',
        sizeClasses
      ]"
    >
      <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
      <template v-for="(item, index) in options" :key="index">
        <optgroup v-if="item.group" :label="item.group">
          <option
            v-for="option in item.options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled"
          >
            {{ option.label }}
          </option>
        </optgroup>
        <option v-else :value="item.value" :disabled="item.disabled">
          {{ item.label }}
        </option>
      </template>
    </select>

    <!-- Searchable/Multi-Select Dropdown -->
    <div v-else class="relative">
      <!-- Trigger Button -->
      <button
        ref="triggerButton"
        type="button"
        @click="toggleDropdown"
        :disabled="disabled || loading"
        :class="[
          'relative w-full rounded-md shadow-sm transition-colors text-left',
          'border border-gray-300 dark:border-gray-600',
          'bg-white dark:bg-gray-800',
          'text-gray-900 dark:text-gray-100',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
          disabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-900' : 'cursor-pointer',
          error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '',
          sizeClasses
        ]"
      >
        <span class="flex items-center justify-between">
          <span class="block truncate">
            <span v-if="loading" class="text-gray-500">Loading...</span>
            <span v-else-if="selectedLabels.length === 0" class="text-gray-500">
              {{ placeholder || 'Select...' }}
            </span>
            <span v-else-if="multiple && selectedLabels.length > 2" class="flex flex-wrap gap-1">
              {{ selectedLabels.length }} selected
            </span>
            <span v-else-if="multiple" class="flex flex-wrap gap-1">
              <Badge
                v-for="label in selectedLabels.slice(0, 2)"
                :key="label"
                size="sm"
                variant="primary"
              >
                {{ label }}
              </Badge>
            </span>
            <span v-else>{{ selectedLabels[0] }}</span>
          </span>
          <svg
            :class="[
              'h-5 w-5 text-gray-400 transition-transform',
              isOpen ? 'transform rotate-180' : ''
            ]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      <!-- Dropdown (Teleported to body) -->
      <Teleport to="body">
        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="transform opacity-0 scale-95"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0 scale-95"
        >
          <div
            v-if="isOpen"
            ref="dropdownMenu"
            :style="dropdownStyle"
            class="fixed z-50 rounded-md bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
          >
          <!-- Search Input -->
          <div v-if="searchable" class="p-2 border-b border-gray-200 dark:border-gray-700">
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              placeholder="Search..."
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click.stop
            />
          </div>

          <!-- Options List -->
          <div class="max-h-60 overflow-auto py-1">
            <template v-if="filteredOptions.length > 0">
              <template v-for="(item, index) in filteredOptions" :key="index">
                <!-- Option Group -->
                <div v-if="item.group">
                  <div class="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {{ item.group }}
                  </div>
                  <button
                    v-for="option in item.options"
                    :key="option.value"
                    type="button"
                    @click="selectOption(option)"
                    :disabled="option.disabled"
                    :class="[
                      'w-full text-left px-3 py-2 flex items-center justify-between transition-colors',
                      option.disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer',
                      isSelected(option.value)
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'text-gray-900 dark:text-gray-100'
                    ]"
                  >
                    <span class="flex items-center flex-1">
                      <input
                        v-if="multiple"
                        type="checkbox"
                        :checked="isSelected(option.value)"
                        class="mr-2 rounded text-blue-600 pointer-events-none"
                      />
                      {{ option.label }}
                    </span>
                    <svg
                      v-if="!multiple && isSelected(option.value)"
                      class="h-5 w-5 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Regular Option -->
                <button
                  v-else
                  type="button"
                  @click="selectOption(item)"
                  :disabled="item.disabled"
                  :class="[
                    'w-full text-left px-3 py-2 flex items-center justify-between transition-colors',
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer',
                    isSelected(item.value)
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'text-gray-900 dark:text-gray-100'
                  ]"
                >
                  <span class="flex items-center flex-1">
                    <input
                      v-if="multiple"
                      type="checkbox"
                      :checked="isSelected(item.value)"
                      class="mr-2 rounded text-blue-600 pointer-events-none"
                    />
                    {{ item.label }}
                  </span>
                  <svg
                    v-if="!multiple && isSelected(item.value)"
                    class="h-5 w-5 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </template>
            </template>

            <!-- No Results -->
            <div v-else class="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No options found
            </div>
          </div>
          </div>
        </Transition>
      </Teleport>
    </div>

    <!-- Help Text -->
    <p v-if="help && !error" class="mt-1 text-sm text-gray-500 dark:text-gray-400">
      {{ help }}
    </p>

    <!-- Error Message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import Badge from './Badge.vue';

const props = defineProps({
  modelValue: {
    type: [String, Number, Array],
    default: null,
  },
  options: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  help: {
    type: String,
    default: null,
  },
  error: {
    type: String,
    default: null,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const inputId = ref(`select-${Math.random().toString(36).substring(2, 9)}`);
const isOpen = ref(false);
const searchQuery = ref('');
const searchInput = ref(null);
const triggerButton = ref(null);
const dropdownMenu = ref(null);
const dropdownStyle = ref({});

/**
 * Get size classes
 */
const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-base',
    lg: 'px-4 py-2.5 text-lg',
  };
  return sizes[props.size];
});

/**
 * Get selected labels for display
 */
const selectedLabels = computed(() => {
  if (props.multiple) {
    const values = Array.isArray(props.modelValue) ? props.modelValue : [];
    return values.map(value => {
      const option = findOption(value);
      return option ? option.label : value;
    }).filter(Boolean);
  } else {
    const option = findOption(props.modelValue);
    return option ? [option.label] : [];
  }
});

/**
 * Filter options based on search query
 */
const filteredOptions = computed(() => {
  if (!searchQuery.value) return props.options;

  const query = searchQuery.value.toLowerCase();

  return props.options
    .map(item => {
      if (item.group) {
        const filteredGroupOptions = item.options.filter(option =>
          option.label.toLowerCase().includes(query)
        );
        return filteredGroupOptions.length > 0
          ? { ...item, options: filteredGroupOptions }
          : null;
      } else {
        return item.label.toLowerCase().includes(query) ? item : null;
      }
    })
    .filter(Boolean);
});

/**
 * Find option by value
 */
const findOption = (value) => {
  for (const item of props.options) {
    if (item.group) {
      const found = item.options.find(opt => opt.value === value);
      if (found) return found;
    } else if (item.value === value) {
      return item;
    }
  }
  return null;
};

/**
 * Check if option is selected
 */
const isSelected = (value) => {
  if (props.multiple) {
    const values = Array.isArray(props.modelValue) ? props.modelValue : [];
    return values.includes(value);
  }
  return props.modelValue === value;
};

/**
 * Handle standard select change
 */
const handleChange = (event) => {
  const value = event.target.value;
  emit('update:modelValue', value);
  emit('change', value);
};

/**
 * Calculate dropdown position
 */
const updateDropdownPosition = () => {
  if (!triggerButton.value) return;

  const rect = triggerButton.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const dropdownHeight = 250; // Max height of dropdown (max-h-60 = 15rem = 240px)

  // Determine if dropdown should open upward or downward
  const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

  dropdownStyle.value = {
    position: 'fixed',
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    ...(openUpward
      ? { bottom: `${window.innerHeight - rect.top}px` }
      : { top: `${rect.bottom + 4}px` }
    ),
  };
};

/**
 * Toggle dropdown
 */
const toggleDropdown = () => {
  if (props.disabled || props.loading) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      updateDropdownPosition();
      if (props.searchable) {
        searchInput.value?.focus();
      }
    });
  }
};

/**
 * Select option
 */
const selectOption = (option) => {
  if (option.disabled) return;

  if (props.multiple) {
    const values = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    const index = values.indexOf(option.value);

    if (index > -1) {
      values.splice(index, 1);
    } else {
      values.push(option.value);
    }

    emit('update:modelValue', values);
    emit('change', values);
  } else {
    emit('update:modelValue', option.value);
    emit('change', option.value);
    isOpen.value = false;
  }
};

/**
 * Close dropdown when clicking outside
 */
const handleClickOutside = (event) => {
  const target = event.target;
  if (!target.closest(`#${inputId.value}`) && !target.closest('.relative')) {
    isOpen.value = false;
  }
};

/**
 * Handle scroll and resize to reposition dropdown
 */
const handleScrollResize = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }
};

/**
 * Watch for open state changes
 */
watch(isOpen, (newValue) => {
  if (newValue) {
    searchQuery.value = '';
    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScrollResize, true);
    window.addEventListener('resize', handleScrollResize);
  } else {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', handleScrollResize, true);
    window.removeEventListener('resize', handleScrollResize);
  }
});

onMounted(() => {
  // Cleanup on unmount is handled in onBeforeUnmount
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('scroll', handleScrollResize, true);
  window.removeEventListener('resize', handleScrollResize);
});
</script>
